import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatPrice } from "@/lib/api";
import { CheckCircle2, Package, Truck, MapPin } from "lucide-react";

export default function OrderConfirmed() {
    const { orderId } = useParams();
    const [order, setOrder] = useState(null);

    useEffect(() => {
        api.get(`/orders/${orderId}`).then(res => setOrder(res.data));
    }, [orderId]);

    if (!order) return <div className="max-w-3xl mx-auto py-32 text-center">Loading…</div>;

    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-10" data-testid="order-confirmed">
            <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }} className="mx-auto h-20 w-20 rounded-full bg-[#2F7D5A] text-white flex items-center justify-center mb-6">
                <CheckCircle2 className="h-10 w-10" />
            </motion.div>
            <h1 className="font-display text-5xl text-center">Order confirmed.</h1>
            <p className="text-center text-muted-foreground mt-3">Thank you. The X-Plosiv team is on it.</p>

            <div className="mt-10 dw-section p-6">
                <div className="flex flex-wrap justify-between items-center gap-3">
                    <div>
                        <p className="font-mono-ui text-muted-foreground">Order number</p>
                        <p className="font-medium" data-testid="order-number">{order.order_number}</p>
                    </div>
                    <Link to={`/track/${order.order_number}`} className="dw-btn-secondary px-5 py-2 font-mono-ui" data-testid="order-track-link">Track order</Link>
                </div>
            </div>

            <div className="mt-6 dw-section p-6">
                <h3 className="font-display text-2xl mb-4">Items</h3>
                <div className="space-y-3">
                    {order.items.map((it, i) => (
                        <div key={i} className="flex gap-4 items-center">
                            <img src={it.image} alt="" className="h-16 w-16 rounded-xl object-cover" />
                            <div className="flex-1">
                                <p className="font-medium">{it.product_title}</p>
                                <p className="text-sm text-muted-foreground">{it.color} / {it.size} · Qty {it.qty}</p>
                            </div>
                            <p className="font-medium">{formatPrice(it.subtotal)}</p>
                        </div>
                    ))}
                </div>
                <div className="h-px bg-[#E7DED2] my-5" />
                <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span>Subtotal</span><span>{formatPrice(order.subtotal)}</span></div>
                    {order.discount > 0 && <div className="flex justify-between text-[#2F7D5A]"><span>Discount</span><span>− {formatPrice(order.discount)}</span></div>}
                    <div className="flex justify-between"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : formatPrice(order.shipping)}</span></div>
                    <div className="flex justify-between font-semibold text-lg pt-2"><span>Total paid</span><span>{formatPrice(order.total)}</span></div>
                </div>
            </div>

            <div className="mt-6 dw-section p-6">
                <h3 className="font-display text-2xl mb-4 flex items-center gap-2"><MapPin className="h-5 w-5" /> Delivery</h3>
                <p className="text-sm">
                    {order.address.full_name}<br />
                    {order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}<br />
                    {order.address.city}, {order.address.state} {order.address.pincode}<br />
                    {order.address.country} · {order.address.phone}
                </p>
            </div>

            <div className="mt-10 flex justify-center gap-3">
                <Link to="/shop" className="dw-btn-secondary px-7 py-3 font-mono-ui">Continue shopping</Link>
                <Link to="/account" className="dw-btn-primary px-7 py-3 font-mono-ui">View my orders</Link>
            </div>
            <div className="h-24" />
        </div>
    );
}
